import _ from 'underscore';
import { featureFlagClient } from '@trello/feature-flag-client';

import {
  MembersFilter,
  LabelsFilter,
  DueFilter,
  CompleteFilter,
  UrlParams,
  BoardsFilter,
  ListFilter,
  SortFilter,
  TitleFilter,
  ModeFilter,
} from './filters';
import { CardFilterCriteria, ViewFilter } from './filters/ViewFilter';

import {
  FilterableCard,
  FilterMode,
  CustomFields,
  Card,
  ChecklistItem,
} from './types';

import { getWords } from 'app/src/satisfies-filter';
import { CustomFieldItem } from './CustomFieldItem';
import { RouteContext } from 'app/src/router';

export interface ViewFiltersParams {
  boards?: BoardsFilter;
  due?: DueFilter;
  labels?: LabelsFilter;
  list?: ListFilter;
  members?: MembersFilter;
  mode?: ModeFilter;
  sort?: SortFilter;
  title?: TitleFilter;
}

// Utility for aggregation convenience functions.
// FilterMode isn't countable, so it is excluded from aggregated totals.
const COUNTABLE_VIEW_FILTER_PARAMS: Array<
  Exclude<keyof ViewFiltersParams, 'mode'>
> = ['boards', 'due', 'labels', 'list', 'members', 'sort', 'title'];
export type CountableViewFilterParams = typeof COUNTABLE_VIEW_FILTER_PARAMS;

export class ViewFilters {
  public boards: BoardsFilter;
  public due: DueFilter;
  public labels: LabelsFilter;
  public list: ListFilter;
  public members: MembersFilter;
  public mode: ModeFilter;
  public calendarDateRange: `${string}...${string}`;
  public sort: SortFilter;
  public title: TitleFilter;

  constructor(params: ViewFiltersParams = {}) {
    this.boards = params.boards || new BoardsFilter();
    this.due = params.due || new DueFilter();
    this.labels = params.labels || new LabelsFilter();
    this.list = params.list || new ListFilter();
    this.members = params.members || new MembersFilter();
    this.mode = params.mode || new ModeFilter();
    this.sort = params.sort || new SortFilter();
    this.title = params.title || new TitleFilter();
  }

  setFilter(filter: ViewFilter): ViewFilters {
    const newViewFilters = new ViewFilters(this);
    if (typeof filter === 'number') {
      newViewFilters['mode'] = filter;
    }

    switch (filter.filterType) {
      case 'boards':
        newViewFilters['boards'] = filter;
        break;
      case 'due':
        newViewFilters['due'] = filter;
        break;
      case 'labels':
        newViewFilters['labels'] = filter;
        break;
      case 'list':
        newViewFilters['list'] = filter;
        break;
      case 'members':
        newViewFilters['members'] = filter;
        break;
      case 'sort':
        newViewFilters['sort'] = filter;
        break;
      case 'title':
        newViewFilters['title'] = filter;
        break;
      default:
        break;
    }

    return newViewFilters;
  }

  clearFilters(
    skip: (keyof ViewFiltersParams)[] = ['boards', 'sort'],
  ): ViewFilters {
    return new ViewFilters(
      Object.fromEntries(skip.map((key) => [key, this[key]])),
    );
  }

  /**
   * Convenience function to determine whether filters are active.
   * `skip` params can be configured to omit certain filters from the check,
   * e.g. a value of ['boards'] checks whether non-boards filters are active.
   */
  isFiltering(skip: CountableViewFilterParams = []): boolean {
    const skipSet = new Set(skip);
    return COUNTABLE_VIEW_FILTER_PARAMS.some(
      (key) => !skipSet.has(key) && !this[key].isEmpty(),
    );
  }

  /**
   * Convenience function to count the total number of active filters.
   * `skip` params can be configured to omit certain filters from the check,
   * e.g. a value of ['boards'] excludes boards from the count.
   */
  totalFilterLength(skip: CountableViewFilterParams = []): number {
    const skipSet = new Set(skip);
    return COUNTABLE_VIEW_FILTER_PARAMS.reduce((acc, key) => {
      if (!skipSet.has(key)) {
        acc += this[key].filterLength();
      }
      return acc;
    }, 0);
  }

  satisfiesBritaFilter(filterable: FilterableCard): boolean {
    const isAnd = this.mode.getMode() === FilterMode.And;

    const activeFilters = [];

    if (!this.labels.isEmpty()) {
      activeFilters.push(
        this.labels.satisfiesLabelsFilter(filterable.labels, isAnd),
      );
    }

    if (!this.members.isEmpty()) {
      activeFilters.push(
        this.members.satisfiesMembersFilter(filterable.idMembers, isAnd),
      );
    }

    if (!this.due.isEmpty()) {
      activeFilters.push(
        this.due.satisfiesDueFilter({
          due: filterable.due,
          complete: filterable.complete,
        }),
      );
    }

    if (!this.title.isEmpty()) {
      activeFilters.push(this.title.satisfiesTitleFilter(filterable.words));
    }

    if (activeFilters.length === 0) {
      return true;
    }

    return isAnd ? activeFilters.every(Boolean) : activeFilters.some(Boolean);
  }

  satisfiesNonBritaFilter(filterable: FilterableCard): boolean {
    const isAnd = this.mode.getMode() === FilterMode.And;

    if (!this.labels.satisfiesLabelsFilter(filterable.labels, isAnd)) {
      return false;
    }

    if (!this.members.satisfiesMembersFilter(filterable.idMembers, isAnd)) {
      return false;
    }

    if (
      !this.due.satisfiesDueFilter({
        due: filterable.due,
        complete: filterable.complete,
      })
    ) {
      return false;
    }

    if (!this.title.satisfiesTitleFilter(filterable.words)) {
      return false;
    }

    return true;
  }

  satisfiesFilter(filterable: FilterableCard): boolean {
    // Received feedback on this updated logic, so changing to a new feature flag at the
    // moment to limit "moving the cheese", and will invest greater thought into the
    // best way to implement this moving forward.
    const isBritaModeEnabled = featureFlagClient.get(
      'remarkable.brita-mode-logic',
      false,
    );

    // Bifurcating for the sake of keeping updates to and/or filtering logic
    // behind a feature flag. Once Brita is fully rolled out, satisfiesNonBritaFilter
    // will be removed, and satisfiesFilter will just be satisfiesBritaFilter

    // Logic difference:
    // NonBrita takes the and/or parameter and only applies it within labels and members.
    // ex: and -> must satisfy green label and red label, but has no bearing on due or title
    // Brita takes the and/or parameter and applies it across all categories.
    // ex: and -> must satisfy green label and Michael Member and title = 'search term'
    return isBritaModeEnabled
      ? this.satisfiesBritaFilter(filterable)
      : this.satisfiesNonBritaFilter(filterable);
  }

  checkAdvancedChecklistItem({
    name,
    state,
    due,
    idMember,
  }: ChecklistItem): boolean {
    const filterableChecklistItem = {
      idMembers: idMember ? [idMember] : [],
      labels: [],
      due: due ? new Date(due) : null,
      complete:
        state === 'complete'
          ? CompleteFilter.Complete
          : CompleteFilter.Incomplete,
      words: getWords(name),
    };

    return this.satisfiesFilter(filterableChecklistItem);
  }

  checkFilterableCard(
    card: Pick<
      Card,
      'idMembers' | 'labels' | 'due' | 'dueComplete' | 'name' | 'idShort'
    > &
      Partial<Pick<Card, 'customFieldItems'>>,
    customFields: CustomFields,
    isCustomFieldsEnabled: boolean,
  ): boolean {
    const {
      idMembers,
      labels,
      due,
      dueComplete,
      name,
      idShort,
      customFieldItems,
    } = card;
    const filterableCustomFieldWords = customFieldItems?.map(
      (customFieldItem) => {
        const filterableCustomFieldItem = new CustomFieldItem(customFieldItem);

        const mappedCustomField = filterableCustomFieldItem.getCustomField(
          customFields,
        );

        if (!mappedCustomField) {
          return undefined;
        }

        return filterableCustomFieldItem.getFilterableWords(mappedCustomField);
      },
    );

    const filterableCard = {
      idMembers,
      labels: labels?.map(({ color, name }) => ({ color, name })),
      due: due ? new Date(due) : null,
      complete: dueComplete
        ? CompleteFilter.Complete
        : CompleteFilter.Incomplete,
      words: _.chain([
        getWords(name),
        getWords(idShort?.toString()),
        isCustomFieldsEnabled ? filterableCustomFieldWords : undefined,
      ])
        .compact()
        .flatten()
        .value(),
    };

    return this.satisfiesFilter(filterableCard);
  }

  toQueryParams(): UrlParams {
    if (this.isFiltering()) {
      const queryParams: UrlParams = {};

      const { labels } = this.labels.toUrlParams();
      const { idMembers } = this.members.toUrlParams();
      const { due, dueComplete } = this.due.toUrlParams();
      const { title } = this.title.toUrlParams();
      const { idBoards } = this.boards.toUrlParams();
      const { mode } = this.mode.toUrlParams();
      const { idLists } = this.list.toUrlParams();
      const { sort } = this.sort.toUrlParams();

      if (idBoards) {
        queryParams.idBoards = idBoards;
      }
      if (labels) {
        queryParams.labels = labels;
      }
      if (idMembers) {
        queryParams.idMembers = idMembers;
      }
      if (due) {
        queryParams.due = due;
      }
      if (dueComplete) {
        queryParams.dueComplete = dueComplete;
      }
      if (title) {
        queryParams.title = title;
      }
      if (mode) {
        queryParams.mode = mode;
      }
      if (idLists) {
        queryParams.idLists = idLists;
      }
      if (sort) {
        queryParams.sort = sort;
      }

      return queryParams;
    }

    return {};
  }

  static fromQueryParams(urlParams: UrlParams) {
    const viewsFilter: ViewFilters = new ViewFilters();

    viewsFilter.due.fromUrlParams(urlParams);
    viewsFilter.members.fromUrlParams(urlParams);
    viewsFilter.labels.fromUrlParams(urlParams);
    viewsFilter.title.fromUrlParams(urlParams);
    viewsFilter.boards.fromUrlParams(urlParams);
    viewsFilter.sort.fromUrlParams(urlParams);
    viewsFilter.list.fromUrlParams(urlParams);
    viewsFilter.mode.fromUrlParams(urlParams);

    return viewsFilter;
  }

  static fromSavedView(cardFilterCriteria: CardFilterCriteria): ViewFilters {
    const viewFilters: ViewFilters = new ViewFilters();

    viewFilters.boards.deserializeFromView(cardFilterCriteria);
    viewFilters.due.deserializeFromView(cardFilterCriteria);
    viewFilters.members.deserializeFromView(cardFilterCriteria);
    viewFilters.list.deserializeFromView(cardFilterCriteria);
    viewFilters.labels.deserializeFromView(cardFilterCriteria);
    viewFilters.sort.deserializeFromView(cardFilterCriteria);
    viewFilters.title.deserializeFromView(cardFilterCriteria);

    return viewFilters;
  }

  toCardFilterCriteria(idBoards?: Array<string>): CardFilterCriteria {
    const cardFilters: CardFilterCriteria = {};

    const { idLists } = this.list.serializeToView();
    const { idMembers } = this.members.serializeToView();
    const { labels } = this.labels.serializeToView();
    const { due, dueComplete } = this.due.serializeToView();
    const { sort } = this.sort.serializeToView();

    if (idBoards) {
      cardFilters.idBoards = idBoards;
    }
    if (idLists) {
      cardFilters.idLists = idLists;
    }
    if (idMembers) {
      cardFilters.idMembers = idMembers;
    }
    if (labels) {
      cardFilters.labels = labels;
    }
    if (due) {
      cardFilters.due = due;
    }
    if (dueComplete !== null) {
      cardFilters.dueComplete = dueComplete;
    }
    if (sort) {
      cardFilters.sort = sort;
    }

    return cardFilters;
  }

  static constructNewUrl = function (
    routeContext: RouteContext,
  ): ((viewFilters: ViewFilters) => URL) | undefined {
    if (!routeContext.url) {
      return undefined;
    }

    // This will keep populate as a param but remove all other params.
    let search = '';
    if (routeContext.url.searchParams.has('populate')) {
      search = `?${new URLSearchParams({ populate: '' }).toString()}`;
    }

    const newUrl = new URL(
      routeContext.url.origin + routeContext.url.pathname + search,
    );

    return function constructUrlFilterParams(viewFilters: ViewFilters) {
      const newUrlParams = {
        ...viewFilters.toQueryParams(),
      };

      for (const [key, value] of Object.entries(newUrlParams)) {
        if (value) {
          newUrl.searchParams.set(key, value);
        }
      }

      // Hack: The URL api auto-encodes commas and colons, but we want to use "invalid" urls
      // with `,` and `:` characters in them.
      newUrl.search = newUrl.search.replace(/%2C/g, ',').replace(/%3A/g, ':');
      return newUrl;
    };
  };
}
